import { useState } from "react";
import { costCalculatorData } from "../../../mocks/costCalculator";

export default function CostCalculatorSection() {
  const [area, setArea] = useState<number>(80);
  const [floors, setFloors] = useState<number>(2);
  const [methodKey, setMethodKey] = useState<keyof typeof costCalculatorData.methodPrices>("steel");
  const [copied, setCopied] = useState(false);
  const [lineSharing, setLineSharing] = useState(false);

  const method = costCalculatorData.methodPrices[methodKey];
  const multiplier = costCalculatorData.floorMultipliers[floors];

  const minTotal = Math.round(area * floors * method.min * multiplier);
  const maxTotal = Math.round(area * floors * method.max * multiplier);
  const minPerPing = Math.round(method.min * multiplier * 10) / 10;
  const maxPerPing = Math.round(method.max * multiplier * 10) / 10;

  const avgTotal = Math.round((minTotal + maxTotal) / 2);
  const fillPercent = Math.min(100, Math.max(15, (avgTotal / 5000) * 100));

  const getShareText = () =>
    `速築美宅家預算試算\n${area} 坪 × ${floors} 層 ｜ ${method.name}\n總預算約 ${minTotal.toLocaleString()} – ${maxTotal.toLocaleString()} 萬元\n每坪約 ${minPerPing}–${maxPerPing} 萬元\n\n試算來源：Chronos Space 速築美宅家 https://chronosspace.com`;

  const handleCopyResult = async () => {
    try {
      await navigator.clipboard.writeText(getShareText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleShareToLine = async () => {
    try {
      await navigator.clipboard.writeText(getShareText());
      setLineSharing(true);
      setTimeout(() => setLineSharing(false), 3000);
    } catch {
      setLineSharing(true);
      setTimeout(() => setLineSharing(false), 3000);
    }
    // Open LINE app or web
    const lineUrl = `https://line.me/R/msg/text/?${encodeURIComponent(getShareText())}`;
    window.open(lineUrl, "_blank");
  };

  return (
    <section className="py-14 md:py-20 bg-warm-gray">
      <div className="container-main px-4 md:px-8 lg:px-12">
        <div className="text-center mb-8 md:mb-10">
          <span className="tag-label mb-3 inline-block">BUDGET ESTIMATE</span>
          <h2 className="mb-3">自地自建預算試算</h2>
          <p className="text-medium-gray max-w-2xl mx-auto">
            選擇坪數、樓層與工法，快速了解預算級距。實際費用會依基地條件與需求調整，建議加入 LINE 諮詢取得精準評估。
          </p>
        </div>

        <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-warm-gray/30 overflow-hidden shadow-sm">
          {/* Input Panel */}
          <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Area */}
            <div>
              <label className="block text-graphite font-semibold text-base mb-3">
                <span className="w-5 h-5 inline-flex items-center justify-center mr-1.5 text-champagne">
                  <i className="ri-ruler-line" />
                </span>
                建築坪數
              </label>
              <div className="flex flex-wrap gap-2">
                {costCalculatorData.areaOptions.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setArea(opt)}
                    className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                      area === opt
                        ? "bg-champagne text-graphite shadow-sm"
                        : "bg-cream text-medium-gray hover:bg-champagne/15 hover:text-graphite"
                    }`}
                  >
                    {opt} 坪
                  </button>
                ))}
              </div>
            </div>

            {/* Floors */}
            <div>
              <label className="block text-graphite font-semibold text-base mb-3">
                <span className="w-5 h-5 inline-flex items-center justify-center mr-1.5 text-champagne">
                  <i className="ri-building-2-line" />
                </span>
                樓層數
              </label>
              <div className="flex flex-wrap gap-2">
                {costCalculatorData.floorOptions.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setFloors(opt)}
                    className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                      floors === opt
                        ? "bg-champagne text-graphite shadow-sm"
                        : "bg-cream text-medium-gray hover:bg-champagne/15 hover:text-graphite"
                    }`}
                  >
                    {opt} 層
                  </button>
                ))}
              </div>
            </div>

            {/* Method */}
            <div>
              <label className="block text-graphite font-semibold text-base mb-3">
                <span className="w-5 h-5 inline-flex items-center justify-center mr-1.5 text-champagne">
                  <i className="ri-hammer-line" />
                </span>
                結構工法
              </label>
              <div className="space-y-2">
                {(Object.keys(costCalculatorData.methodPrices) as Array<keyof typeof costCalculatorData.methodPrices>).map((key) => {
                  const m = costCalculatorData.methodPrices[key];
                  const isActive = methodKey === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setMethodKey(key)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer border ${
                        isActive
                          ? "border-champagne bg-champagne/8 text-graphite"
                          : "border-warm-gray/30 bg-cream text-medium-gray hover:border-champagne/30 hover:bg-champagne/5"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={isActive ? "font-semibold" : ""}>{m.name}</span>
                        <span className="text-xs opacity-70">{m.min}–{m.max} 萬/坪</span>
                      </div>
                      {isActive && (
                        <p className="text-xs text-medium-gray mt-1 leading-snug">{m.desc}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-warm-gray/30" />

          {/* Result Panel */}
          <div className="p-6 md:p-8 bg-cream/50">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
              <div>
                <p className="text-medium-gray text-sm mb-1">
                  {area} 坪 × {floors} 層 ｜ {method.name} ｜ 每坪約 {minPerPing}–{maxPerPing} 萬元
                </p>
                <p className="text-3xl md:text-4xl font-serif font-bold text-graphite">
                  總預算約 <span className="text-champagne">{minTotal.toLocaleString()} – {maxTotal.toLocaleString()}</span> 萬元
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
                <button
                  onClick={handleCopyResult}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap border cursor-pointer ${
                    copied
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : "bg-white border-warm-gray/30 text-graphite hover:border-champagne/40 hover:bg-champagne/5"
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={copied ? "ri-check-line" : "ri-clipboard-line"} />
                  </span>
                  {copied ? "已複製結果" : "複製預算結果"}
                </button>
                <button
                  onClick={handleShareToLine}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap border cursor-pointer ${
                    lineSharing
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : "bg-[#06C755] border-[#06C755] text-white hover:bg-[#05b34d]"
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={lineSharing ? "ri-check-line" : "ri-share-forward-line"} />
                  </span>
                  {lineSharing ? "已複製，請貼到 LINE" : "分享到 LINE"}
                </button>
                <a
                  href="https://lin.ee/OpssTIM"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary gap-2 text-sm"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-line-fill" />
                  </span>
                  取得精準評估
                </a>
              </div>
            </div>

            {/* Visual bar */}
            <div className="relative h-4 bg-warm-gray/40 rounded-full overflow-hidden mb-3">
              <div
                className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-champagne/60 to-champagne transition-all duration-700 ease-out"
                style={{ width: `${fillPercent}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-medium-gray">
              <span>500 萬</span>
              <span>2,500 萬</span>
              <span>5,000 萬+</span>
            </div>

            {/* Notice */}
            <div className="mt-5 p-4 bg-white rounded-xl border border-warm-gray/20">
              <p className="text-sm text-medium-gray leading-relaxed mb-0">
                <span className="inline-flex items-center gap-1.5 text-champagne font-semibold mr-1">
                  <i className="ri-information-line" />
                  試算說明：
                </span>
                以上為建築結構體之參考級距，實際費用會依基地條件（地質、坡度、法規限制）、室內裝修等級、設備規格與特殊需求而調整。不含土地購置費用。建議加入 LINE 提供基地位置與需求，我們將安排專人進行精準評估。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}